import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { AgGridReact } from 'ag-grid-react'; // React Grid Logic
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import "./Grid.css"; // Custom Styles
import axiosInstance from '../../../services/AxiosInstance';
import { successAlert, warnAlert, warnAlertYesOrNo } from '../alert/defaultAlert';
import i18next from 'i18next';
import { AG_GRID_LOCALE_PT_BR } from "./locale/locale-pt_br";
import { ButtonGroup, Col, Dropdown, Row } from "react-bootstrap"
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Eye from '../Common/Eye';
import { convertDoubleToStringPTBR } from '../../../utils/ConverterUtils';
import TrashIcon from '../Icon/TrashIcon';
import EditIcon from '../Icon/EditIcon';
import SaveIcon from '../Icon/SaveIcon';
import { formatDateTime, formatUTCDate } from '../../../utils/DateUtils';
import { ApiError } from '../Common/ApiError';
import { formatCNPJ, formatCPF, formatPhoneNumberOfPhoneComponent } from '../../../utils/FormatUtils';
import { ThemeContext } from '../../../context/ThemeContext';

/**
* @param {boolean} pagination - Exibe ou não a paginação do grid.
* @param {boolean} rowSelection - Permite selecionar uma ou mais linhas.
* @param {number} height - Configura a altera do grid.
* @param {string} columns - Colunas que serão exibidas no grid.
* @param {object} column - Objeto que representa a coluna.
* @param {string} column.width - Largura da coluna em pixels.
* @param {boolean} column.editable - Indica se a coluna é editável ou não.
* @param {boolean} column.checkboxSelection - Exibe o checkbox de seleção.
* @param {string} column.type - Formatação pronta da coluna, definida em columnTypes: currency (R$ com
*                               duas casas), date, datetime, cnpj, cpf, phone, percentage, boolean e
*                               trans. O nome precisa ser exato; um tipo desconhecido é ignorado e o
*                               valor aparece cru.
* @param {boolean} column.filter - Adiciona o filtro na coluna.
* @param {object[]} setSelectedRows - Recebe as linhas selecionadas.
* @param {object} customButtons - Adiciona botões customizados.
* @param {object} beforeGrid - Conteúdo exibido entre a barra de ações e a grade, como a
*                              legenda dos selos de uma listagem: fica colado no que explica,
*                              em vez de ficar acima da barra de ações e longe das linhas.
* @param {boolean} hideInactiveToggle - Esconde o item "Inativos" do menu de ações. Para as telas
*                                        que filtram a situação pelo próprio painel de filtros, onde
*                                        o atalho do menu brigaria com o select.
* @param {boolean} serverSide - Ativa a paginação server-side (padrão: true se pagination=true).
*/
export const Grid = (props) => {

    const { t } = useTranslation();
    const { appTheme = 'dark' } = useContext(ThemeContext) || {};
    const navigate = useNavigate();
    const gridRef = useRef();
    const { setGridRef, filters } = props;
    const [columns] = useState(props.columns);
    const [colDefs, setColDefs] = useState([]);
    const [pagination] = useState(props.pagination);
    const [rowSelection] = useState(props.rowSelection);
    const [path] = useState(props.path);
    const [rowData, setRowData] = useState([]);
    const { route, customButtons, setSelectedRows, hideDefaultButtons, hideRemove, hideInactiveToggle, selectedBranch, multiBranchSupport, onDelete, beforeGrid, removeConfirmMessage } = props;
    const [showInactive, setShowInactive] = useState(false);

    // Flag para ativar paginação no servidor (padrão: true se tiver paginação ativada)
    const serverSide = props.serverSide !== undefined ? props.serverSide : pagination;
    const [gridApi, setGridApi] = useState(null);

    // State para controlar o tamanho da página internamente e forçar refresh do cacheBlockSize
    const defaultPageSizes = props.pageSizeSelector || [20, 50, 100];
    const [internalPageSize, setInternalPageSize] = useState(props.pageSize || defaultPageSizes[0]);

    // Configuração do Datasource para paginação infinita server-side
    const getDataSource = useCallback(() => {
        return {
            getRows: (params) => {
                // Mostra o loading overlay manualmente
                gridRef.current?.api?.showLoadingOverlay();
                
                const { startRow, endRow, sortModel } = params;
                const pageSize = endRow - startRow;
                const page = Math.floor(startRow / pageSize);

                // Prepara os filtros base
                let activeFilters = [];

                // Filtros externos passados via props
                if (filters && filters.length > 0) {
                    activeFilters = [...filters];
                }

                // Filtro de inativos
                if (showInactive) {
                    activeFilters.push({
                        field: "status",
                        value: "INACTIVE",
                        operation: "EQUAL"
                    });
                }

                // Filtro de filial
                if (selectedBranch) {
                    const branchField = multiBranchSupport ? 'branches.id' : 'branch.id';
                    activeFilters.push({
                        field: branchField,
                        value: selectedBranch.id,
                        operation: "EQUAL"
                    });
                }

                // Conversão do SortModel do AG Grid para o Orders do Backend
                const sortOrders = sortModel.map(s => ({
                    field: s.colId,
                    direction: s.sort.toUpperCase()
                }));

                // O que o backend espera como Operation
                const getOperation = (type) => {
                    switch (type) {
                        case 'contains': return 'MATCH';
                        case 'equals': return 'EQUAL';
                        case 'startsWith': return 'MATCH_START'; // Ajuste conforme seu backend
                        case 'endsWith': return 'MATCH_END';     // Ajuste conforme seu backend
                        default: return 'MATCH';
                    }
                };

                // Conversão do FilterModel do AG Grid para os Filters do Backend
                if (params.filterModel) {
                    Object.keys(params.filterModel).forEach(key => {
                        const item = params.filterModel[key];
                        // Suporte básico para filtro de texto
                        if (item.filterType === 'text') {
                            activeFilters.push({
                                field: key,
                                value: item.filter,
                                operation: getOperation(item.type)
                            });
                        }
                        // Você pode expandir para 'number', 'date', etc aqui
                    });
                }

                // Chamada ao Backend (/pageable)
                const url = path + "/pageable";

                axiosInstance.post(url, {
                    filters: activeFilters,
                    page: page,
                    size: pageSize,
                    orders: sortOrders
                })
                    .then(response => {
                        // response.data é um Page<DTO> do Spring
                        // content: array de dados
                        // totalElements: total de registros
                        params.successCallback(response.data.content, response.data.totalElements);
                    })
                    .catch(error => {
                        ApiError(error);
                        params.failCallback();
                    })
                    .finally(() => {
                        // Esconde o loading overlay após conclusão
                        gridRef.current?.api?.hideOverlay();
                    });
            }
        };
    }, [path, filters, showInactive, selectedBranch, multiBranchSupport]);

    const getData = useCallback((currentFilters) => {
        gridRef.current?.api?.showLoadingOverlay();

        let tmpFilters = (currentFilters) ? currentFilters : [];
        if (showInactive) {
            tmpFilters = [
                {
                    field: "status",
                    value: "INACTIVE",
                    operation: "EQUAL"
                }
            ]
        }

        if (selectedBranch) {
            if (multiBranchSupport) {
                tmpFilters.push({
                    field: 'branches.id',
                    value: selectedBranch.id,
                    operation: "EQUAL"
                });
            } else {
                tmpFilters.push({
                    field: 'branch.id',
                    value: selectedBranch.id,
                    operation: "EQUAL"
                });
            }
        }

        axiosInstance.post(path + "/findByFilters", {
            filters: tmpFilters
        })
            .then((response) => {
                setRowData(response.data);
            })
            .catch(error => {
                ApiError(error);
            })
            .finally(() => {
                gridRef.current?.api?.hideOverlay();
            });
    }, [path, showInactive, selectedBranch, multiBranchSupport]);

    // Logs removidos.
    // Preeche o grid com os dados obtidos via API.
    const onGridReady = useCallback((params) => {
        setGridApi(params.api);

        if (!columns) {
            alert("It's necessary to inform the grid columns");
            return;
        }

        let cols = columns.map((column) => ({
            field: column.field,
            headerValueGetter: () => i18next.t(column.headerName),
            width: column.width,
            checkboxSelection: column.checkboxSelection,
            headerCheckboxSelection: column.headerCheckboxSelection,
            editable: column.editable,
            valueFormatter: column.valueFormatter,
            valueGetter: column.valueGetter,
            cellRenderer: column.cellRenderer,
            filter: column.filter,
            type: column?.type,
            cellStyle: column.cellStyle,
            // Colunas calculadas fora da entidade precisam desligar a ordenação: o
            // backend monta o ORDER BY pelo nome do campo e não encontraria o atributo.
            sortable: column.sortable !== false,
            comparator: serverSide ? () => 0 : undefined // Desativa sort client-side se for server-side
        }));

        setColDefs(cols);

        if (setGridRef) {
            setGridRef(gridRef);
        }

        if (!serverSide) {
            getData(filters);
        } else {
            // Em infinite row model, o datasource é setado aqui
            params.api.setGridOption('datasource', getDataSource());
        }

    }, [columns, serverSide, getDataSource, getData, filters, setGridRef]);

    // Monitora mudança de paginação para atualizar o cacheBlockSize (recriando o grid via key)
    const onPaginationChanged = useCallback((params) => {
        if (params.newPageSize) {
            const newSize = params.api.paginationGetPageSize();
            if (newSize !== internalPageSize) {
                setInternalPageSize(newSize);
            }
        }
    }, [internalPageSize]);

    // Atualiza o datasource/dados quando filtros mudam
    useEffect(() => {
        if (gridApi) {
            if (serverSide) {
                gridApi.setGridOption('datasource', getDataSource());
                // Força reload pois o datasource mudou
                // gridApi.refreshInfiniteCache(); // Não necessário se setDatasource já reseta
            } else {
                getData(filters);
            }
        }
    }, [showInactive, selectedBranch, filters, serverSide, gridApi, getDataSource, getData]);

    // Acionado quando é clicado na coluna
    const onCellClicked = useCallback((params) => {
        // console.log('Cell was clicked', params)        
    }, []);

    // Responsável por atualizar a linha de uma célula editada.
    const onCellValueChanged = (params) => {
        console.log("Update id: " + params.data.id + " | ColId: " + params.column.getColId());
    }

    // Acionado quando uma ou mais linhas são selecionados.
    const onSelectionChanged = (params) => {
        const selectedRows = params.api.getSelectedRows();
        setSelectedRows?.(selectedRows);
    }

    const handleNew = () => {
        navigate(route + "/new");
    }

    // Remove um registro do grid.
    const handleRemove = () => {
        const selectedRows = gridRef.current?.api.getSelectedRows() || [];

        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }

        // A tela pode trocar a pergunta quando remover tem consequência que o texto padrão não
        // conta (Planos: o plano tem assinantes e é excluído também no provedor de pagamento).
        warnAlertYesOrNo((removeConfirmMessage && removeConfirmMessage(selectedRows)) || t('message.remove_the_selected_item'), () => {
            Promise.all(selectedRows.map(row => axiosInstance.delete(props.path + "/" + row.id)))
                .then(() => {
                    successAlert(t('message.removed_successfully'));

                    if (serverSide) {
                        gridApi.refreshInfiniteCache();
                        // Limpa seleção no modo infinite
                        gridApi.deselectAll();
                    } else {
                        gridRef.current?.api.applyTransaction({
                            remove: selectedRows,
                        });
                    }

                    if (onDelete) {
                        selectedRows.forEach(row => onDelete(row.id));
                    }
                })
                .catch(error => ApiError(error))
        });
    }

    // Redireciona para o formulário passando o id
    const handleUpdate = () => {
        const selectedRows = gridRef.current?.api.getSelectedRows() || [];

        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }
        if (selectedRows.length > 1) {
            warnAlert(t('validation.required.row_select_one'));
            return;
        }
        navigate(route + "/new/" + selectedRows[0].id);
    }

    // Formatters
    const currencyFormatter = (params) => {
        const value = params.value;
        // isNaN(null) é false: sem o teste de nulo, a célula vazia virava "R$ null".
        if (value == null || isNaN(value)) {
            return "";
        }
        return "R$ " + convertDoubleToStringPTBR(value);
    }

    const transFormatter = (params) => {
        const value = params.value;
        return value ? i18next.t(value.toLowerCase()) : '';
    }

    const dateFormatter = (params) => {
        const value = params.value;
        return formatUTCDate(value);
    }

    const dateTimeFormatter = (params) => {
        if (params.value) {
            const value = params.value;
            return formatDateTime(value);
        }
        return "-"
    }

    const cnpjFormatter = (params) => {
        const value = params.value;
        return formatCNPJ(value);
    }

    const cpfFormatter = (params) => {
        const value = params.value;
        return formatCPF(value);
    }

    const percentageFormatter = (params) => {
        const value = params.value;
        if (value == null) {
            return "";
        }
        return convertDoubleToStringPTBR(value) + "%";
    }

    const phoneFormatter = (params) => {
        const value = params.value;
        return formatPhoneNumberOfPhoneComponent(value);
    };

    const booleanRenderer = (params) => {
        const value = params.value;
        if (value === true || value === 'true') {
            return <span style={{ color: '#22c55e', fontSize: '1.05rem' }}><i className="fas fa-check-circle"></i></span>;
        }
        return <span style={{ color: '#cbd5e1', fontSize: '1.05rem' }}><i className="fas fa-times-circle"></i></span>;
    };

    const columnTypes = useMemo(() => {
        return {
            boolean: {
                width: 120,
                cellRenderer: booleanRenderer,
                cellStyle: { textAlign: 'center' },
                headerClass: 'ag-col-center',
            },
            currency: {
                width: 150,
                valueFormatter: currencyFormatter,
            },
            trans: {
                width: 150,
                valueFormatter: transFormatter,
            },
            date: {
                width: 150,
                valueFormatter: dateFormatter
            },
            datetime: {
                width: 150,
                valueFormatter: dateTimeFormatter
            },
            cnpj: {
                width: 200,
                valueFormatter: cnpjFormatter
            },
            cpf: {
                width: 200,
                valueFormatter: cpfFormatter
            },
            percentage: {
                width: 150,
                valueFormatter: percentageFormatter
            },
            phone: {
                width: 150,
                valueFormatter: phoneFormatter
            }
        };
    }, []);

    const localeText = useMemo(() => {
        return AG_GRID_LOCALE_PT_BR;
    }, []);

    return (
        <>
            {
                <Row className='mb-3'>
                    <Col md={12}>
                        <ButtonGroup>
                            <Dropdown>
                                <Dropdown.Toggle className="me-2" variant="primary">
                                    {t('actions')}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item hidden={hideDefaultButtons} onClick={handleNew}><SaveIcon /> {t('new')}</Dropdown.Item>
                                    <Dropdown.Item hidden={hideDefaultButtons} onClick={handleUpdate}><EditIcon /> {t('update')}</Dropdown.Item>
                                    {/* hideRemove existe para as listas em que apagar não é uma
                                        operação válida, mas criar e editar continuam sendo. */}
                                    <Dropdown.Item hidden={hideDefaultButtons || hideRemove} onClick={handleRemove}><TrashIcon /> {t('remove')}</Dropdown.Item>
                                    {/* Some quando a tela já tem um filtro de situação próprio: os dois
                                        mandariam um filtro de status cada um, e a busca se anularia. */}
                                    <Dropdown.Item hidden={hideDefaultButtons || hideInactiveToggle} onClick={() => { setShowInactive(!showInactive) }}><Eye open={showInactive} /> {t('inactives')}</Dropdown.Item>
                                    {(customButtons) ? customButtons : ''}
                                </Dropdown.Menu>
                            </Dropdown>
                        </ButtonGroup>
                    </Col>
                </Row>
            }

            {beforeGrid}

            <div
                style={{ height: props.height }}
                className={`colmeia-grid ${appTheme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'}`}
            >
                {/* Mantém a seleção nativa do navegador disponível para copiar texto das células. */}
                <AgGridReact
                    key={serverSide ? internalPageSize : 'client-side'}
                    ref={gridRef}
                    rowData={serverSide ? undefined : rowData}
                    columnDefs={colDefs}
                    pagination={pagination}
                    paginationPageSize={internalPageSize}
                    paginationPageSizeSelector={defaultPageSizes}
                    cacheBlockSize={internalPageSize}
                    infiniteInitialRowCount={internalPageSize}
                    rowModelType={serverSide ? 'infinite' : 'clientSide'}
                    rowSelection={rowSelection}
                    rowHeight={props.rowHeight}
                    enableCellTextSelection={true}
                    onGridReady={onGridReady}
                    onCellValueChanged={onCellValueChanged}
                    onSelectionChanged={onSelectionChanged}
                    onCellClicked={onCellClicked}
                    onPaginationChanged={onPaginationChanged}
                    reactiveCustomComponents={true}
                    columnTypes={columnTypes}
                    localeText={localeText}
                    overlayLoadingTemplate={`
                        <div class="ag-custom-loading">
                            <i class="fas fa-spinner fa-spin"></i>
                            <span>${t('loading_data')}</span>
                        </div>
                    `}
                />
            </div>
        </>
    )

}
export default Grid;
